import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { adminApiService } from '../../services/adminApi';

// ---- date helpers (admin is in Thailand → browser-local date is fine) ----
const todayStr = () => new Date().toLocaleDateString('en-CA');
const shift = (str, n) => {
  const d = new Date(str + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString('en-CA');
};
const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString('en-CA');
};
const baht = (n) => '฿' + Number(n || 0).toLocaleString();

const PRESETS = [
  { id: 'today', label: 'วันนี้', range: () => ({ from: todayStr(), to: todayStr() }) },
  { id: '7', label: '7 วัน', range: () => ({ from: shift(todayStr(), -6), to: todayStr() }) },
  { id: '30', label: '30 วัน', range: () => ({ from: shift(todayStr(), -29), to: todayStr() }) },
  { id: 'month', label: 'เดือนนี้', range: () => ({ from: monthStart(), to: todayStr() }) },
];

// Literal class names so Tailwind's purge keeps them (no dynamic `text-${x}`).
const TONE = {
  emerald: 'text-emerald-700',
  indigo: 'text-indigo-700',
  violet: 'text-violet-700',
  sky: 'text-sky-700',
};
const KpiCard = ({ label, value, sub, tone = 'indigo' }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
    <p className="text-xs font-medium text-gray-500">{label}</p>
    <p className={`mt-1 text-2xl font-bold ${TONE[tone] || 'text-gray-900'}`}>{value}</p>
    {sub && <p className="mt-1 text-xs text-gray-400">{sub}</p>}
  </div>
);

const Dashboard = () => {
  const { adminUser } = useAdmin();
  const idToken = adminUser?.idToken;

  const [preset, setPreset] = useState('30');
  const [range, setRange] = useState(PRESETS.find((p) => p.id === '30').range());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ledger
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const applyPreset = (id) => {
    setPreset(id);
    if (id !== 'custom') setRange(PRESETS.find((p) => p.id === id).range());
  };

  const loadStats = useCallback(async () => {
    if (!idToken) return;
    setLoading(true);
    setError(null);
    try {
      const data = await adminApiService.getStats(idToken, range);
      setStats(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [idToken, range]);

  const loadLedger = useCallback(async (reset = true) => {
    if (!idToken) return;
    reset ? setRows([]) : setLoadingMore(true);
    try {
      const data = await adminApiService.getPayments(idToken, {
        ...range,
        limit: 25,
        cursor: reset ? undefined : cursor,
      });
      setRows((prev) => (reset ? data.rows : [...prev, ...data.rows]));
      setCursor(data.nextCursor);
      setHasMore(data.hasMore);
    } catch (e) {
      // ledger errors are non-fatal for the KPIs
      console.error('ledger load failed', e);
    } finally {
      setLoadingMore(false);
    }
  }, [idToken, range, cursor]);

  useEffect(() => { loadStats(); }, [loadStats]);
  // reload ledger from scratch whenever the range changes
  useEffect(() => { loadLedger(true); /* eslint-disable-next-line */ }, [idToken, range.from, range.to]);

  const exportCsv = () => {
    const head = ['วันที่', 'ผู้ใช้', 'ประเภท', 'ช่องทาง', 'สนาม', 'จำนวนเงิน', 'ชั่วโมง', 'สถานะ'];
    const lines = rows.map((r) => [
      new Date(r.createdAt).toLocaleString('en-CA'),
      r.user, r.type, r.channel, r.courtType, r.amount, r.hours, r.status,
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const csv = [head.join(','), ...lines].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `payments_${range.from}_${range.to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const maxDaily = stats ? Math.max(1, ...stats.daily.map((d) => d.booking + d.topup)) : 1;

  const channelLabel = { qr: 'QR', credit: 'เครดิต', topup: 'เติมเงิน' };
  const statusLabel = { completed: 'สำเร็จ', pending: 'รอ', failed: 'ล้มเหลว', cancelled: 'ยกเลิก' };

  return (
    <div>
      {/* Range picker */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            onClick={() => applyPreset(p.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              preset === p.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {p.label}
          </button>
        ))}
        <div className="flex items-center gap-1.5 ml-auto">
          <input type="date" value={range.from} max={range.to}
            onChange={(e) => { setPreset('custom'); setRange((r) => ({ ...r, from: e.target.value })); }}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
          <span className="text-gray-400 text-sm">–</span>
          <input type="date" value={range.to} min={range.from} max={todayStr()}
            onChange={(e) => { setPreset('custom'); setRange((r) => ({ ...r, to: e.target.value })); }}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm" />
        </div>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3">{error}</div>}

      {loading && !stats ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : stats ? (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <KpiCard label="💰 รายรับเงินสด" tone="emerald"
              value={baht(stats.revenue.cashTotal)}
              sub={`จอง ${baht(stats.revenue.booking)} · เติม ${baht(stats.revenue.topup)}`} />
            <KpiCard label="⏱️ ชั่วโมงที่ถูกใช้" tone="indigo"
              value={`${stats.credit.hours.toFixed(1)} ชม.`}
              sub={`≈ ${baht(stats.credit.value)} มูลค่า`} />
            <KpiCard label="📅 การจอง" tone="violet"
              value={`${stats.bookings.total}`}
              sub={`QR ${stats.bookings.qr} · เครดิต ${stats.bookings.credit} · แอดมิน ${stats.bookings.admin}`} />
            <KpiCard label="👥 ผู้ใช้ใหม่" tone="sky"
              value={`+${stats.users.new}`}
              sub={`รวม ${stats.users.total} คน`} />
          </div>

          {/* Daily revenue bars */}
          <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">รายรับรายวัน</h3>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> จองตรง</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-indigo-400" /> เติมเครดิต</span>
              </div>
            </div>
            <div className="flex items-end gap-0.5 h-32 overflow-x-auto">
              {stats.daily.map((d) => {
                const total = d.booking + d.topup;
                return (
                  <div key={d.date} className="flex-1 min-w-[6px] flex flex-col justify-end h-full"
                    title={`${d.date}\nจองตรง ${baht(d.booking)}\nเติม ${baht(d.topup)}`}>
                    <div className="bg-indigo-400 rounded-t-sm" style={{ height: `${(d.topup / maxDaily) * 100}%` }} />
                    <div className="bg-emerald-500" style={{ height: `${(d.booking / maxDaily) * 100}%` }} />
                    {total === 0 && <div className="h-px bg-gray-100" />}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-[10px] text-gray-400 mt-1">
              <span>{stats.daily[0]?.date}</span>
              <span>{stats.daily[stats.daily.length - 1]?.date}</span>
            </div>
          </div>

          {/* Payment ledger */}
          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">รายงานการชำระเงิน</h3>
              <button onClick={exportCsv} disabled={!rows.length}
                className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-300">
                ⬇ ดาวน์โหลด CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs">
                  <tr>
                    <th className="text-left font-medium px-4 py-2">วันที่</th>
                    <th className="text-left font-medium px-4 py-2">ผู้ใช้</th>
                    <th className="text-left font-medium px-4 py-2">ประเภท</th>
                    <th className="text-left font-medium px-4 py-2">ช่องทาง</th>
                    <th className="text-left font-medium px-4 py-2">สนาม</th>
                    <th className="text-right font-medium px-4 py-2">จำนวน</th>
                    <th className="text-left font-medium px-4 py-2">สถานะ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 whitespace-nowrap text-gray-600">{new Date(r.createdAt).toLocaleDateString('th-TH', { day: '2-digit', month: 'short' })} {new Date(r.createdAt).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-gray-800">{r.user}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{r.type === 'topup' ? 'เติม' : 'จอง'}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{channelLabel[r.channel] || r.channel}</td>
                      <td className="px-4 py-2 whitespace-nowrap capitalize text-gray-600">{r.courtType}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-right font-medium">
                        {r.channel === 'credit' ? `${r.hours.toFixed(1)} ชม.` : baht(r.amount)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          r.status === 'completed' ? 'bg-green-100 text-green-700' :
                          r.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-500'
                        }`}>{statusLabel[r.status] || r.status}</span>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">ไม่มีรายการในช่วงนี้</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {hasMore && (
              <div className="p-3 border-t border-gray-100 text-center">
                <button onClick={() => loadLedger(false)} disabled={loadingMore}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700">
                  {loadingMore ? 'กำลังโหลด…' : 'โหลดเพิ่ม'}
                </button>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default Dashboard;
